/**
 * SafeMotion - SSR-safe framer-motion wrapper
 * 
 * Prevents hydration mismatches by skipping initial animations during SSR.
 * On the server and during hydration, components render in their "visible" state.
 * After hydration, animations work normally.
 */

import { createContext, useContext, ReactNode } from 'react';
import { useIsClient } from '../../hooks/useIsClient';

// Context to track if we should animate
const AnimationContext = createContext(false);

/**
 * Provider that enables animations only after client hydration
 */
export function SafeMotionProvider({ children }: { children: ReactNode }) {
  const isClient = useIsClient();
  
  return (
    <AnimationContext.Provider value={isClient}>
      {children}
    </AnimationContext.Provider>
  );
}

/**
 * Hook to check if animations should run
 * Returns false during SSR and initial hydration
 * Returns true after the component has mounted on the client
 */
export function useCanAnimate(): boolean {
  return useContext(AnimationContext);
}

/**
 * Hook to get SSR-safe animation props
 * During SSR/hydration: returns the "visible" state to prevent mismatch
 * After hydration: returns normal animation props
 */
export function useSafeAnimation<T extends Record<string, unknown>>(
  initial: T,
  animate: T,
  inView: boolean = true
): { initial: T | undefined; animate: T | undefined } {
  const canAnimate = useCanAnimate();
  
  if (!canAnimate) {
    // During SSR/hydration, render in final state (no animation)
    return { initial: undefined, animate: undefined };
  }
  
  // After hydration, animate normally
  return {
    initial,
    animate: inView ? animate : initial,
  };
}

/**
 * Hook to get SSR-safe variants animation props
 * For use with containerVariants/itemVariants pattern
 */
export function useSafeVariants(
  inView: boolean = true
): { initial: string | undefined; animate: string } {
  const canAnimate = useCanAnimate();
  
  if (!canAnimate) {
    // During SSR/hydration, render in visible state immediately
    // Using 'visible' ensures children with itemVariants are shown
    return { initial: undefined, animate: 'visible' };
  }
  
  return {
    initial: 'hidden',
    animate: inView ? 'visible' : 'hidden',
  };
}
